import { DateTime } from "luxon";
import type {
  ManagementDashboardQueryParams,
  ManagementDashboardResponse,
  ManagementDashboardStatusIdApi
} from "../typings/management-dashboard.types";
import type { ManagementPeriodMode } from "./management-period-picker.utils";
import { formatManagementChartDayLabel } from "./management-period-picker.utils";
import {
  filterManagementPeriodDaysByBookingWeekends,
  type ManagementBookingWeekendSettings
} from "./management-period-weekend.utils";

export type ManagementStatusLabelKey =
  | "_management_statusOffice"
  | "_management_statusMobile"
  | "_management_statusNotSpecified";

export type ManagementDashboardViewModel = {
  companyName: string;
  totalUsers: number;
  locations: Array<{ id: string; name: string }>;
  departments: Array<{ id: string; name: string }>;
  occupancyTodayPercent: number;
  occupiedSeats: number;
  freeSeats: number;
  inOfficeCount: number;
  plannedOfficeCount: number;
  checkedInCount: number;
  locationRanking: Array<{
    id: string;
    name: string;
    occupancyPercent: number;
    occupiedSeats: number;
    freeSeats: number;
  }>;
  statusBreakdown: Array<{
    id: ManagementDashboardStatusIdApi;
    labelKey: ManagementStatusLabelKey;
    percent: number;
    count: number;
  }>;
  occupancyPeriodDays: Array<{
    dateLabel: string;
    occupancyPercent: number;
    occupiedSeats: number;
    freeSeats: number;
  }>;
  statusesPeriodDays: Array<{
    dateLabel: string;
    officeCount: number;
    officePercent: number;
    mobileWorkCount: number;
    mobileWorkPercent: number;
    notSpecifiedCount: number;
    notSpecifiedPercent: number;
  }>;
  insights: Array<{
    templateKey: string;
    params: Record<string, string | number>;
    cta?: "heatmap" | "hrReport";
  }>;
};

const STATUS_LABEL_KEYS: Record<ManagementDashboardStatusIdApi, ManagementStatusLabelKey> = {
  office: "_management_statusOffice",
  mobileWork: "_management_statusMobile",
  notSpecified: "_management_statusNotSpecified"
};

export function mapManagementDashboardResponse(
  response: ManagementDashboardResponse,
  periodMode: ManagementPeriodMode,
  weekendSettings: ManagementBookingWeekendSettings = {},
  language = "en"
): ManagementDashboardViewModel {
  const occupancyPeriodDays = filterManagementPeriodDaysByBookingWeekends(
    response.occupancyPeriodDays,
    weekendSettings
  );
  const statusesPeriodDays = filterManagementPeriodDaysByBookingWeekends(
    response.statusesPeriodDays,
    weekendSettings
  );
  const dayOfMonthOnly = periodMode === "month";

  return {
    companyName: response.companyName,
    totalUsers: response.totalUsers,
    locations: response.locations,
    departments: response.departments,
    occupancyTodayPercent: response.occupancyTodayPercent,
    occupiedSeats: response.occupiedSeats,
    freeSeats: response.freeSeats,
    inOfficeCount: response.inOfficeCount,
    plannedOfficeCount: response.plannedOfficeCount,
    checkedInCount: response.checkedInCount,
    locationRanking: response.locationRanking,
    statusBreakdown: response.statusBreakdown.map(item => ({
      id: item.statusId,
      labelKey: STATUS_LABEL_KEYS[item.statusId],
      percent: item.percent,
      count: item.count
    })),
    occupancyPeriodDays: occupancyPeriodDays.map(day => ({
      dateLabel: formatManagementChartDayLabel(day.date, language, dayOfMonthOnly),
      occupancyPercent: day.occupancyPercent,
      occupiedSeats: day.occupiedSeats,
      freeSeats: day.freeSeats
    })),
    statusesPeriodDays: statusesPeriodDays.map(day => ({
      dateLabel: formatManagementChartDayLabel(day.date, language, dayOfMonthOnly),
      officeCount: day.office.count,
      officePercent: day.office.percent,
      mobileWorkCount: day.mobileWork.count,
      mobileWorkPercent: day.mobileWork.percent,
      notSpecifiedCount: day.notSpecified.count,
      notSpecifiedPercent: day.notSpecified.percent
    })),
    insights: response.insights.map(insight => ({
      templateKey: insight.templateKey,
      params: insight.params,
      cta: insight.cta
    }))
  };
}

export function buildManagementDashboardQueryParams(input: {
  periodMode: ManagementPeriodMode;
  periodAnchor: DateTime;
  asOfDate?: string;
  /** Empty = all locations (Pattern A). */
  locationIds: string[];
  /** Empty = all departments (Pattern A). */
  departmentIds: string[];
}): ManagementDashboardQueryParams {
  const periodStart =
    input.periodMode === "week"
      ? input.periodAnchor.startOf("week").toISODate()
      : input.periodAnchor.startOf("month").toISODate();

  const params: ManagementDashboardQueryParams = {
    periodMode: input.periodMode,
    periodStart: periodStart ?? input.periodAnchor.toISODate() ?? "",
    asOfDate: input.asOfDate
  };

  if (input.locationIds.length > 0) {
    const locationInventoryIds = input.locationIds
      .map(id => Number(id))
      .filter(id => Number.isFinite(id));
    if (locationInventoryIds.length > 0) {
      params.locationInventoryIds = locationInventoryIds;
    }
  }

  if (input.departmentIds.length > 0) {
    params.departmentIds = input.departmentIds;
  }

  return params;
}
